import { ChangeDetectionStrategy, Component, computed, input, output, signal, ViewEncapsulation } from '@angular/core';
import { ButtonDirective } from '../button/button.directive';
import {
  AccordionVariantProps,
  accordionBodyVariants,
  accordionEmptyVariants,
  accordionHeaderVariants,
  accordionMetaVariants,
  accordionTitleVariants,
  accordionToggleIconVariants,
  accordionVariants,
} from './accordion.variants';

@Component({
  selector: 'lib-accordion',
  imports: [ButtonDirective],
  templateUrl: './accordion.component.html',
  styleUrl: './accordion.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  host: {
    '[class]': 'hostClass()',
  },
})
export class AccordionComponent {
  readonly variant = input<AccordionVariantProps['variant']>('docs');
  readonly collapsible = input(true);
  readonly showHeader = input(true);
  readonly collapsedPlaceholder = input(false);
  readonly collapsed = input<boolean | undefined>(undefined);
  readonly collapsedChange = output<boolean>();
  readonly class = input('');

  readonly #internalCollapsed = signal(false);

  protected readonly isCollapsed = computed(() => {
    const controlled = this.collapsed();
    return controlled !== undefined ? controlled : this.#internalCollapsed();
  });

  protected readonly isHeaderlessSecondary = computed(() => this.variant() === 'secondary' && !this.showHeader());

  protected readonly wrapsBody = computed(() => !this.isHeaderlessSecondary());

  protected readonly panelClasses = computed(() =>
    accordionVariants({
      variant: this.variant(),
      shell: !this.isHeaderlessSecondary(),
    })
  );

  protected readonly isShellCollapsed = computed(
    () => this.collapsible() && this.isCollapsed() && !this.collapsedPlaceholder()
  );

  protected readonly headerClasses = computed(() =>
    accordionHeaderVariants({
      variant: this.variant(),
      actions: this.collapsible() || this.variant() === 'secondary' || this.variant() === 'field',
      shellCollapsed: this.isShellCollapsed(),
    })
  );

  protected readonly titleClasses = computed(() =>
    accordionTitleVariants({
      variant: this.variant(),
    })
  );

  protected readonly bodyClasses = computed(() =>
    accordionBodyVariants({
      variant: this.variant(),
    })
  );

  protected readonly emptyClasses = computed(() =>
    accordionEmptyVariants({
      variant: this.variant(),
    })
  );

  protected readonly metaClasses = computed(() =>
    accordionMetaVariants({
      variant: this.variant(),
    })
  );

  protected readonly toggleIconClasses = computed(() =>
    accordionToggleIconVariants({ expanded: !this.isCollapsed() })
  );

  protected readonly hostClass = computed(() => this.class());

  protected togglePanelCollapse(): void {
    const next = !this.isCollapsed();
    if (this.collapsed() === undefined) {
      this.#internalCollapsed.set(next);
    }
    this.collapsedChange.emit(next);
  }
}
